import React from 'react';
import SafeIcon from '../../common/SafeIcon';
import {
  FiCalendar,
  FiCreditCard,
  FiClock,
  FiCheckCircle,
  FiAlertCircle
} from 'react-icons/fi';

const STATUS_STYLES = {
  active: {
    badge:
      'border-emerald-100 bg-emerald-50 text-emerald-700',
    icon: FiCheckCircle,
    label: 'Active'
  },

  trialing: {
    badge:
      'border-blue-100 bg-blue-50 text-blue-700',
    icon: FiClock,
    label: 'Trial'
  },

  incomplete: {
    badge:
      'border-amber-100 bg-amber-50 text-amber-700',
    icon: FiAlertCircle,
    label: 'Incomplete'
  },

  past_due: {
    badge:
      'border-amber-100 bg-amber-50 text-amber-700',
    icon: FiAlertCircle,
    label: 'Past Due'
  },

  unpaid: {
    badge:
      'border-rose-100 bg-rose-50 text-rose-700',
    icon: FiAlertCircle,
    label: 'Unpaid'
  },

  paused: {
    badge:
      'border-slate-200 bg-slate-100 text-slate-600',
    icon: FiClock,
    label: 'Paused'
  },

  canceled: {
    badge:
      'border-slate-200 bg-slate-100 text-slate-500',
    icon: FiAlertCircle,
    label: 'Canceled'
  }
};

const formatStatus = (value) => {
  if (!value) {
    return '';
  }

  return String(value)
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (letter) =>
      letter.toUpperCase()
    );
};

const formatDate = (value) => {
  if (!value) {
    return '—';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '—';
  }

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium'
  }).format(date);
};

export default function BillingSubscriptionCard({
  subscription
}) {
  const hasSubscription =
    Boolean(subscription);

  const status = String(
    subscription?.status || ''
  ).toLowerCase();

  const statusConfig =
    STATUS_STYLES[status] || {
      badge:
        'border-slate-200 bg-slate-50 text-slate-500',
      icon: FiClock,
      label: formatStatus(status)
    };

  const StatusIcon =
    statusConfig.icon;

  const periodStart =
    subscription?.current_period_start;

  const periodEnd =
    subscription?.current_period_end;

  const interval =
    subscription?.billing_interval;

  const isCancellationScheduled =
    Boolean(
      subscription?.cancel_at_period_end
    );

  return (
    <section className="overflow-hidden rounded-[32px] border border-slate-200 bg-white shadow-sm">
      {/* Header */}
      <div className="border-b border-slate-100 bg-gradient-to-br from-slate-50 via-white to-white p-6 md:p-7">
        <div className="flex flex-col justify-between gap-5 sm:flex-row sm:items-start">
          <div>
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">
              Current subscription
            </p>

            <h2 className="mt-2 text-2xl font-black tracking-tight text-slate-950">
              {subscription?.plan_name ||
                'No subscription'}
            </h2>

            <p className="mt-1 text-sm text-slate-500">
              {hasSubscription
                ? 'Your organization subscription and billing status.'
                : 'Choose a plan below to get started.'}
            </p>
          </div>

          {hasSubscription &&
            status && (
              <div
                className={`inline-flex w-fit items-center gap-2 rounded-full border px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.12em] ${statusConfig.badge}`}
              >
                <SafeIcon
                  icon={StatusIcon}
                />

                {statusConfig.label}
              </div>
            )}
        </div>
      </div>

      {/* Content */}
      <div className="p-6 md:p-7">
        {!hasSubscription ? (
          <div className="flex items-center gap-4 rounded-2xl border border-dashed border-slate-200 bg-slate-50/70 p-5">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-slate-400 shadow-sm">
              <SafeIcon
                icon={FiCreditCard}
              />
            </div>

            <div>
              <p className="text-sm font-black text-slate-700">
                No active subscription
              </p>

              <p className="mt-1 text-xs font-medium leading-5 text-slate-400">
                Select a plan below to start your ProfitYO
                subscription.
              </p>
            </div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">

              {/* Billing */}
              <div className="rounded-2xl border border-slate-100 bg-slate-50/70 p-5 transition-colors hover:bg-slate-50">
                <div className="mb-3 flex items-center gap-2 text-xs font-bold text-slate-400">
                  <SafeIcon
                    icon={FiCreditCard}
                  />

                  Billing
                </div>

                <p className="text-base font-black capitalize text-slate-900">
                  {interval || '—'}
                </p>
              </div>

              {/* Period */}
              <div className="rounded-2xl border border-slate-100 bg-slate-50/70 p-5 transition-colors hover:bg-slate-50 sm:col-span-2">
                <div className="mb-3 flex items-center gap-2 text-xs font-bold text-slate-400">
                  <SafeIcon
                    icon={FiCalendar}
                  />

                  Current billing period
                </div>

                <p className="text-base font-black text-slate-900">
                  {periodStart ||
                  periodEnd
                    ? `${formatDate(
                        periodStart
                      )} – ${formatDate(
                        periodEnd
                      )}`
                    : '—'}
                </p>
              </div>
            </div>

            {/* Cancellation */}
            {isCancellationScheduled && (
              <div className="mt-5 flex items-start gap-3 rounded-2xl border border-amber-100 bg-amber-50 p-4">
                <SafeIcon
                  icon={FiAlertCircle}
                  className="mt-0.5 shrink-0 text-amber-600"
                />

                <div>
                  <p className="text-sm font-black text-amber-900">
                    Cancellation scheduled
                  </p>

                  <p className="mt-0.5 text-xs font-semibold leading-5 text-amber-700">
                    Your subscription will remain available
                    until the end of the current billing
                    period.
                  </p>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}